import React, { useMemo, useState } from 'react';
import { Alert, Pagination, Table } from 'react-bootstrap';
import { FaSort, FaSortDown, FaSortUp } from 'react-icons/fa';

const fmtMoeda = (value) =>
  Number(value || 0).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const fmtData = (value) => {
  if (!value) return '-';
  const data = new Date(value);
  if (Number.isNaN(data.getTime())) return value;
  return data.toLocaleDateString('pt-BR');
};

// G-COL-01: rótulos do agrupamento por Tipo (paridade UDespesasAnalitico.pas:331-341)
const ROTULO_TIPO = { 0: 'Despesas/Receitas', 1: 'Projeto', 2: 'Carteira' };
const rotuloTipo = (tipo) => ROTULO_TIPO[tipo] || `Tipo ${tipo}`;

const getLinhaKey = (linha) =>
  `${linha.codigo_mata}-${linha.codigo_conta}-${linha.data_mata}-${linha.valor}`;

const getValorOrdenacao = (linha, coluna) => {
  if (coluna === 'data_mata') return linha.data_mata ? new Date(linha.data_mata).getTime() : 0;
  return linha[coluna] != null ? linha[coluna] : '';
};

// G-COL-06: lado contábil de cada linha (débito ou crédito)
const ehDebito = (linha) =>
  linha.codigo_conta_debitada === linha.codigo_conta &&
  linha.codigo_grupo_empresa_deb === linha.codigo_grupo_empresa;

const TOTAL_COLUNAS = 11;

const TabelaAgrupada = ({ linhas, totais }) => {
  // G-COL-12: ordem default por codigo_grupo_empresa (paridade Delphi pas:417-420)
  const [ordenacao, setOrdenacao] = useState({ coluna: 'codigo_grupo_empresa', direcao: 'asc' });
  const [paginaAtual, setPaginaAtual] = useState(1);
  const itensPorPagina = 50;

  const linhasOrdenadas = useMemo(() => {
    const copia = [...(linhas || [])];
    copia.sort((a, b) => {
      const valorA = getValorOrdenacao(a, ordenacao.coluna);
      const valorB = getValorOrdenacao(b, ordenacao.coluna);
      if (typeof valorA === 'number' && typeof valorB === 'number') {
        return ordenacao.direcao === 'asc' ? valorA - valorB : valorB - valorA;
      }
      const textoA = String(valorA).toLowerCase();
      const textoB = String(valorB).toLowerCase();
      if (textoA < textoB) return ordenacao.direcao === 'asc' ? -1 : 1;
      if (textoA > textoB) return ordenacao.direcao === 'asc' ? 1 : -1;
      return 0;
    });
    return copia;
  }, [linhas, ordenacao]);

  const totalPaginas = Math.ceil(linhasOrdenadas.length / itensPorPagina);
  const linhasPagina = useMemo(() => {
    const inicio = (paginaAtual - 1) * itensPorPagina;
    return linhasOrdenadas.slice(inicio, inicio + itensPorPagina);
  }, [linhasOrdenadas, paginaAtual]);

  // G-COL-01 + G-COL-02: 4 níveis de agrupamento (GE → Tipo → Conta → Ativo)
  const grupos = useMemo(() => {
    const ges = new Map();
    linhasPagina.forEach((linha) => {
      const chaveGe = `${linha.codigo_grupo_empresa}|${linha.grupo_empresa || 'SEM GE'}`;
      if (!ges.has(chaveGe)) ges.set(chaveGe, new Map());
      const tipos = ges.get(chaveGe);
      const chaveTipo = String(linha.tipo);
      if (!tipos.has(chaveTipo)) tipos.set(chaveTipo, new Map());
      const contas = tipos.get(chaveTipo);
      const chaveConta = `${linha.codigo_conta}|${linha.apelido_conta || ''}`;
      if (!contas.has(chaveConta)) contas.set(chaveConta, new Map());
      const ativos = contas.get(chaveConta);
      const chaveAtivo = `${linha.codigo_ativo}|${linha.nome_ativo || ''}`;
      if (!ativos.has(chaveAtivo)) ativos.set(chaveAtivo, []);
      ativos.get(chaveAtivo).push(linha);
    });

    return Array.from(ges.entries()).map(([chaveGe, tipos]) => {
      const [codigoGe, apelidoGe] = chaveGe.split('|');
      return {
        codigoGe: Number(codigoGe),
        apelidoGe,
        tipos: Array.from(tipos.entries()).map(([chaveTipo, contas]) => ({
          tipo: Number(chaveTipo),
          contas: Array.from(contas.entries()).map(([chaveConta, ativos]) => {
            const [codigoConta, apelidoConta] = chaveConta.split('|');
            return {
              codigoConta: Number(codigoConta),
              apelidoConta,
              ativos: Array.from(ativos.entries()).map(([chaveAtivo, linhasAtivo]) => {
                const [codigoAtivo, nomeAtivo] = chaveAtivo.split('|');
                return {
                  codigoAtivo: Number(codigoAtivo),
                  nomeAtivo,
                  saldo: linhasAtivo[0] && linhasAtivo[0].saldo,
                  linhas: linhasAtivo,
                };
              }),
            };
          }),
        })),
      };
    });
  }, [linhasPagina]);

  const alterarOrdenacao = (coluna) => {
    setOrdenacao((atual) => ({
      coluna,
      direcao: atual.coluna === coluna && atual.direcao === 'asc' ? 'desc' : 'asc',
    }));
    setPaginaAtual(1);
  };

  const renderSort = (coluna) => {
    if (ordenacao.coluna !== coluna) return <FaSort className="ml-1" />;
    return ordenacao.direcao === 'asc' ? <FaSortUp className="ml-1" /> : <FaSortDown className="ml-1" />;
  };

  const renderHeader = (label, coluna, align) => (
    <th
      style={{ cursor: 'pointer', textAlign: align || 'left', whiteSpace: 'nowrap' }}
      onClick={() => alterarOrdenacao(coluna)}
    >
      {label}
      {renderSort(coluna)}
    </th>
  );

  // G-COL-03: subtotais por Ativo separados em débito/crédito
  const calcSubtotalAtivo = (linhasAtivo) => {
    let totalDeb = 0;
    let totalCre = 0;
    let totalConv = 0;
    let totalMo11 = 0;
    linhasAtivo.forEach((l) => {
      const valor = Number(l.valor || 0);
      if (ehDebito(l)) totalDeb += valor;
      else totalCre += valor;
      totalConv += Number(l.valor_convertido || 0);
      totalMo11 += Number(l.valor_moeda_11 || 0);
    });
    return {
      totalAtivo: totalCre - totalDeb,
      totalDeb,
      totalCre,
      totalConv,
      totalMo11,
    };
  };

  const renderPaginacao = () => {
    if (totalPaginas <= 1) return null;
    const paginas = [];
    const inicio = Math.max(1, paginaAtual - 2);
    const fim = Math.min(totalPaginas, inicio + 4);
    for (let pagina = inicio; pagina <= fim; pagina += 1) {
      paginas.push(
        <Pagination.Item
          key={`pagina-${pagina}`}
          active={pagina === paginaAtual}
          onClick={() => setPaginaAtual(pagina)}
        >
          {pagina}
        </Pagination.Item>
      );
    }
    return (
      <div className="d-flex justify-content-between align-items-center flex-wrap mt-2">
        <span className="small text-muted">
          Exibindo {linhasPagina.length} de {linhasOrdenadas.length} lançamento(s)
        </span>
        <Pagination className="mb-0">
          <Pagination.Prev
            disabled={paginaAtual === 1}
            onClick={() => setPaginaAtual(Math.max(1, paginaAtual - 1))}
          />
          {paginas}
          <Pagination.Next
            disabled={paginaAtual === totalPaginas}
            onClick={() => setPaginaAtual(Math.min(totalPaginas, paginaAtual + 1))}
          />
        </Pagination>
      </div>
    );
  };

  if (!linhas || linhas.length === 0) {
    return <Alert variant="info" className="mt-3">Nenhuma informação encontrada.</Alert>;
  }

  return (
    <div className="mt-3">
      <div style={{ overflowX: 'auto' }}>
        <Table striped bordered hover size="sm" style={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
          <thead className="thead-dark">
            <tr>
              {renderHeader('Data', 'data_mata')}
              {renderHeader('Histórico', 'observacao')}
              {renderHeader('C.P.', 'contrapartida')}
              {renderHeader('Lançador', 'nome_usuario')}
              {renderHeader('Débito', 'valor', 'right')}
              {renderHeader('Crédito', 'valor', 'right')}
              {renderHeader('Conv.', 'valor_convertido', 'right')}
              {renderHeader('MO 11', 'valor_moeda_11', 'right')}
              {renderHeader('C.C./Depto.', 'centro_custo_departamento')}
              {renderHeader('Lançamento', 'codigo_mata')}
              {renderHeader('Aprovador', 'aprovador')}
            </tr>
          </thead>
          <tbody>
            {grupos.map(({ codigoGe, apelidoGe, tipos }) => (
              <React.Fragment key={`ge-${codigoGe}`}>
                {/* G-COL-10: cabeçalho GE com código + apelido */}
                <tr style={{ backgroundColor: '#d6e4f7' }}>
                  <td colSpan={TOTAL_COLUNAS}>
                    <strong>GE: {codigoGe} - {apelidoGe}</strong>
                  </td>
                </tr>
                {tipos.map(({ tipo, contas }) => (
                  <React.Fragment key={`ge-${codigoGe}-tipo-${tipo}`}>
                    {/* G-COL-01: cabeçalho Tipo de Conta */}
                    <tr style={{ backgroundColor: '#c8d4e8' }}>
                      <td colSpan={TOTAL_COLUNAS}>
                        <strong>{rotuloTipo(tipo)}</strong>
                      </td>
                    </tr>
                    {contas.map(({ codigoConta, apelidoConta, ativos }) => (
                      <React.Fragment key={`ge-${codigoGe}-tipo-${tipo}-conta-${codigoConta}`}>
                        {/* G-COL-09: cabeçalho Conta com código + apelido + GE */}
                        <tr style={{ backgroundColor: '#eaf0fb' }}>
                          <td colSpan={TOTAL_COLUNAS}>
                            <strong>Conta: {codigoConta} - {apelidoConta} | GE: {codigoGe}</strong>
                          </td>
                        </tr>
                        {ativos.map(({ codigoAtivo, nomeAtivo, saldo, linhas: linhasAtivo }) => {
                          const sub = calcSubtotalAtivo(linhasAtivo);
                          return (
                            <React.Fragment key={`ge-${codigoGe}-tipo-${tipo}-conta-${codigoConta}-at-${codigoAtivo}`}>
                              {/* G-COL-02: cabeçalho Ativo com Saldo Atual */}
                              <tr style={{ backgroundColor: '#f1f5fb' }}>
                                <td colSpan={TOTAL_COLUNAS}>
                                  <em>Moeda: {codigoAtivo} {nomeAtivo} | Saldo Atual: {fmtMoeda(saldo)}</em>
                                </td>
                              </tr>
                              {linhasAtivo.map((linha) => {
                                const debito = ehDebito(linha);
                                const obsExtras = [linha.observacao1, linha.observacao2, linha.observacao3]
                                  .filter((o) => o && o.trim());
                                return (
                                  <React.Fragment key={getLinhaKey(linha)}>
                                    <tr>
                                      <td>{fmtData(linha.data_mata)}</td>
                                      <td>{linha.observacao || '-'}</td>
                                      {/* G-COL-04 */}
                                      <td>{linha.contrapartida || '-'}</td>
                                      {/* G-COL-07 opção A: Lançador (paridade Delphi pas:195) */}
                                      <td>{linha.nome_usuario || '-'}</td>
                                      {/* G-COL-06: Débito × Crédito separados */}
                                      <td className="text-right">{debito ? fmtMoeda(linha.valor) : ''}</td>
                                      <td className="text-right">{!debito ? fmtMoeda(linha.valor) : ''}</td>
                                      <td className="text-right">{fmtMoeda(linha.valor_convertido)}</td>
                                      <td className="text-right">{fmtMoeda(linha.valor_moeda_11)}</td>
                                      {/* G-COL-05 */}
                                      <td>{linha.centro_custo_departamento || '-'}</td>
                                      <td>{linha.codigo_mata || '-'}</td>
                                      <td>{linha.aprovador || '-'}</td>
                                    </tr>
                                    {/* G-COL-08: sub-linhas com Obs1/2/3 não-nulas */}
                                    {obsExtras.map((obs, idx) => (
                                      <tr
                                        key={`${getLinhaKey(linha)}-obs-${idx}`}
                                        style={{ backgroundColor: '#fafbfd' }}
                                      >
                                        <td></td>
                                        <td colSpan={TOTAL_COLUNAS - 1} style={{ paddingLeft: '24px', fontStyle: 'italic', color: '#555' }}>
                                          {obs}
                                        </td>
                                      </tr>
                                    ))}
                                  </React.Fragment>
                                );
                              })}
                              {/* G-COL-03: subtotais por Ativo (T.At., Tot.Déb., Tot.Créd.) */}
                              <tr style={{ backgroundColor: '#f3f6fb' }}>
                                <td colSpan={4} className="text-right">
                                  <strong>T.At. ({nomeAtivo}):</strong>
                                </td>
                                <td className="text-right">
                                  <strong>{fmtMoeda(sub.totalDeb)}</strong>
                                </td>
                                <td className="text-right">
                                  <strong>{fmtMoeda(sub.totalCre)}</strong>
                                </td>
                                <td className="text-right">
                                  <strong>{fmtMoeda(sub.totalConv)}</strong>
                                </td>
                                <td className="text-right">
                                  <strong>{fmtMoeda(sub.totalMo11)}</strong>
                                </td>
                                <td colSpan={3} className="text-right">
                                  <small>Saldo movimento: <strong>{fmtMoeda(sub.totalAtivo)}</strong></small>
                                </td>
                              </tr>
                            </React.Fragment>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </React.Fragment>
                ))}
              </React.Fragment>
            ))}
            {/* Total geral por moeda (GAP-04 do AUDITORIA_GAPS) */}
            {totais && totais.total_geral_por_moeda && Object.keys(totais.total_geral_por_moeda).length > 0 && (
              Object.entries(totais.total_geral_por_moeda).map(([moeda, total]) => (
                <tr key={`total-mo-${moeda}`} style={{ backgroundColor: '#2c5aa0', color: '#fff' }}>
                  <td colSpan={6} className="text-right">
                    <strong>Total geral MO {moeda}:</strong>
                  </td>
                  <td className="text-right">
                    <strong>{fmtMoeda(total)}</strong>
                  </td>
                  <td colSpan={4}></td>
                </tr>
              ))
            )}
            <tr style={{ backgroundColor: '#1a3a6b', color: '#fff' }}>
              <td colSpan={6} className="text-right">
                <strong>TOTAL GERAL MO 11:</strong>
              </td>
              <td colSpan={2} className="text-right">
                <strong>
                  {fmtMoeda(totais && totais.total_moeda_11 != null
                    ? totais.total_moeda_11
                    : linhasOrdenadas.reduce((acc, l) => acc + Number(l.valor_moeda_11 || 0), 0))}
                </strong>
              </td>
              <td colSpan={3}></td>
            </tr>
          </tbody>
        </Table>
      </div>
      {renderPaginacao()}
    </div>
  );
};

export default TabelaAgrupada;
